import { useEffect, useMemo, useState } from "react";
import { Link, Route, Routes, useLocation, useNavigate } from "react-router";
import { Button, LocaleSwitcher, Switch, cn } from "@bloom/ui";
import { makeT } from "@bloom/i18n";
import { useLocale } from "../locale/locale-context.tsx";
import { useAuth } from "../auth/auth-context.tsx";
import { api, ApiError } from "../api/client.ts";

interface PurchaseSummary {
  id: string;
  kind: "pack" | "bundle";
  seriesId: string;
  seriesSlug: string | null;
  seriesTitleEn: string | null;
  seriesTitleTh: string | null;
  seriesPosterUrl: string | null;
  episodeIds: string[] | null;
  episodeCount: number | null;
  amountThb: number;
  paymentMethod: "card" | "promptpay" | null;
  status: string;
  paidAt?: string;
  // D7 — the row now ships a flag for whether a receipt exists; the actual
  // signed URL is minted on demand by GET /api/me/purchases/:id/receipt.
  hasReceipt: boolean;
  receiptPdfUrl: string | null;
  createdAt: string;
}

// Tab labels are looked up in `packages/i18n/src/catalog.ts` under the
// `account.tab.*` namespace. Adding a tab → add a new catalog key in both
// locales and append it here.
const TABS = [
  { value: "profile", to: "/account", labelKey: "account.tab.profile" },
  { value: "wallet", to: "/account/wallet", labelKey: "account.tab.wallet" },
  { value: "purchases", to: "/account/purchases", labelKey: "account.tab.purchases" },
  { value: "watch-history", to: "/account/watch-history", labelKey: "account.tab.watch_history" },
  { value: "devices", to: "/account/devices", labelKey: "account.tab.devices" },
  { value: "security", to: "/account/security", labelKey: "account.tab.security" },
  { value: "notifications", to: "/account/notifications", labelKey: "account.tab.notifications" },
  { value: "language", to: "/account/language", labelKey: "account.tab.language" },
  { value: "data-privacy", to: "/account/data-privacy", labelKey: "account.tab.data_privacy" },
] as const;

export function AccountPage() {
  const { user, logout, loading } = useAuth();
  const { t } = useLocale();
  const { pathname } = useLocation();

  if (loading) return <div className="p-6">…</div>;
  if (!user) {
    return (
      <div className="mx-auto max-w-md p-6 text-center">
        <p>{t("errors.unauthorized")}</p>
        <Link to="/login" className="text-[var(--brand-rose)]">
          {t("auth.login")}
        </Link>
      </div>
    );
  }

  // Active-tab derivation by URL — clicking a tab navigates (real route change)
  // instead of mutating a hidden Tabs value. Bookmarkable + back-button friendly.
  const activeValue = pathname.startsWith("/account/wallet")
    ? "wallet"
    : pathname.startsWith("/account/purchases")
      ? "purchases"
      : pathname.startsWith("/account/watch-history")
        ? "watch-history"
        : pathname.startsWith("/account/devices")
          ? "devices"
          : pathname.startsWith("/account/security")
            ? "security"
            : pathname.startsWith("/account/notifications")
              ? "notifications"
              : pathname.startsWith("/account/data-privacy")
                ? "data-privacy"
                : pathname.startsWith("/account/language")
                  ? "language"
                  : "profile";

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-8 sm:px-6">
      <header className="flex items-baseline justify-between">
        <h1 className="font-display text-4xl">{t("nav.account")}</h1>
        <Button variant="ghost" onClick={logout}>
          {t("nav.logout")}
        </Button>
      </header>

      {/* Router-driven tab strip — each tab is a real Link so the URL reflects
          the active panel. Wraps to a second row on mobile because 9 tabs
          do not fit a single thumb-row at 360px. */}
      <nav className="flex flex-wrap items-center gap-1 rounded-2xl bg-[var(--brand-pink-soft)] p-1 text-sm sm:rounded-full">
        {TABS.map((tab) => {
          const active = activeValue === tab.value;
          return (
            <Link
              key={tab.value}
              to={tab.to}
              className={cn(
                "rounded-full px-4 py-1.5 transition-colors",
                active
                  ? "bg-[var(--brand-ink)] text-white"
                  : "text-[var(--brand-ink)]/70 hover:text-[var(--brand-ink)]",
              )}
            >
              {t(tab.labelKey)}
            </Link>
          );
        })}
      </nav>

      <div className="mt-2">
        <Routes>
          <Route index element={<ProfileTab />} />
          <Route path="wallet" element={<WalletTab />} />
          <Route path="purchases" element={<PurchasesTab />} />
          <Route path="watch-history" element={<WatchHistoryTab />} />
          <Route path="devices" element={<DevicesTab />} />
          <Route path="security" element={<SecurityTab />} />
          <Route path="notifications" element={<NotificationsTab />} />
          <Route path="language" element={<LanguageTab />} />
          <Route path="data-privacy" element={<DataPrivacyTab />} />
        </Routes>
      </div>
    </div>
  );
}

function ProfileTab() {
  const { user } = useAuth();
  const { t } = useLocale();
  return (
    <div className="space-y-4 rounded-2xl border border-[var(--brand-border)] bg-white p-6">
      <header>
        <h2 className="font-heading text-lg font-bold">{t("account.profile.heading")}</h2>
        <p className="text-sm text-[var(--brand-muted)]">{t("account.profile.subheading")}</p>
      </header>
      <dl className="grid gap-4 sm:grid-cols-2">
        <Field label={t("account.profile.field.name")} value={user?.name ?? "—"} />
        <Field label={t("account.profile.field.email")} value={user?.email ?? "—"} />
        <Field
          label={t("account.profile.field.locale")}
          value={user?.locale === "th" ? t("settings.locale.th") : t("settings.locale.en")}
        />
        <Field label={t("account.profile.field.role")} value={user?.role ?? "user"} mono />
      </dl>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-[var(--brand-muted)]">{label}</dt>
      <dd className={cn("mt-1 font-semibold", mono && "font-mono text-sm")}>{value}</dd>
    </div>
  );
}

function PurchasesTab() {
  const { locale, t } = useLocale();
  const [items, setItems] = useState<PurchaseSummary[] | null>(null);
  useEffect(() => {
    void api
      .get<{ items: PurchaseSummary[] }>("/api/me/purchases")
      .then((d) => setItems(d.items))
      .catch(() => setItems([]));
  }, []);

  if (items === null) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-20 animate-pulse rounded-2xl bg-[var(--brand-pink-soft)]" />
        ))}
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-[var(--brand-border)] p-12 text-center">
        <p className="font-heading text-lg font-bold">{t("account.purchases.empty.title")}</p>
        <p className="mt-1 text-sm text-[var(--brand-muted)]">
          {t("account.purchases.empty.description")}
        </p>
        <Link
          to="/series"
          className="mt-4 inline-flex h-10 items-center rounded-full bg-[var(--brand-rose)] px-5 text-sm font-semibold text-white"
        >
          {t("account.purchases.empty.cta")}
        </Link>
      </div>
    );
  }
  return (
    <ul className="space-y-3">
      {items.map((p) => (
        <PurchaseCard key={p.id} purchase={p} locale={locale} />
      ))}
    </ul>
  );
}

function PurchaseCard({ purchase: p, locale }: { purchase: PurchaseSummary; locale: "th" | "en" }) {
  const t = makeT(locale);
  const title =
    (locale === "th" ? p.seriesTitleTh : p.seriesTitleEn) ?? p.seriesTitleEn ?? p.seriesId;
  // Human-readable kind label. Pack = "{n}-episode pack", Bundle = "Whole series".
  const kindLabel =
    p.kind === "bundle"
      ? t("account.purchases.kind.bundle")
      : t("account.purchases.kind.pack", { count: p.episodeCount ?? 0 });
  const paidStr = p.paidAt
    ? new Date(p.paidAt).toLocaleDateString(locale === "th" ? "th-TH" : "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";
  const statusLabel = p.status === "paid" ? t("account.purchases.status.paid") : p.status;
  // PromptPay is a brand name → no translation. Card label is bilingual.
  const methodLabel =
    p.paymentMethod === "card"
      ? t("account.purchases.method.card")
      : p.paymentMethod === "promptpay"
        ? "PromptPay"
        : null;

  return (
    <li className="flex items-center gap-4 rounded-2xl border border-[var(--brand-border)] bg-white p-3 sm:p-4">
      {/* Poster — small 9:16 thumbnail */}
      {p.seriesPosterUrl ? (
        <Link
          to={p.seriesSlug ? `/series/${p.seriesSlug}` : "#"}
          className="block shrink-0 overflow-hidden rounded-lg"
        >
          <img
            src={p.seriesPosterUrl}
            alt={title}
            className="h-20 w-12 object-cover sm:h-24 sm:w-16"
            loading="lazy"
          />
        </Link>
      ) : (
        <div className="h-20 w-12 shrink-0 rounded-lg bg-[var(--brand-pink-soft)] sm:h-24 sm:w-16" />
      )}

      {/* Title block */}
      <div className="min-w-0 flex-1">
        <Link
          to={p.seriesSlug ? `/series/${p.seriesSlug}` : "#"}
          className="block font-heading text-base font-bold text-[var(--brand-ink)] hover:underline sm:text-lg"
        >
          {title}
        </Link>
        <p className="mt-0.5 text-sm text-[var(--brand-muted)]">
          {kindLabel} · {paidStr}
          {methodLabel ? ` · ${methodLabel}` : ""}
        </p>
        <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" /> {statusLabel}
        </p>
      </div>

      {/* Amount + receipt */}
      <div className="shrink-0 text-right">
        <p className="font-mono text-base font-semibold sm:text-lg">฿{p.amountThb.toFixed(0)}</p>
        {p.hasReceipt ? <ReceiptLink purchaseId={p.id} locale={locale} /> : null}
      </div>
    </li>
  );
}

// Receipt download — fetches a fresh 24h signed URL each click instead of
// trusting whatever was persisted. Falls back to legacy receiptPdfUrl on
// failure (older paid rows from before D7 still have the inline data: URL).
function ReceiptLink({ purchaseId, locale }: { purchaseId: string; locale: "th" | "en" }) {
  const t = makeT(locale);
  const [loading, setLoading] = useState(false);
  const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await api.get<{ url: string }>(`/api/me/purchases/${purchaseId}/receipt`);
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("[receipt] signed url failed", err);
      alert(t("account.purchases.receipt.unavailable"));
    } finally {
      setLoading(false);
    }
  };
  return (
    <a
      href="#"
      onClick={handleClick}
      className="mt-1 inline-block text-xs text-[var(--brand-rose)] hover:underline"
    >
      {loading ? t("account.purchases.receipt.opening") : t("account.purchases.receipt.label")}
    </a>
  );
}

function LanguageTab() {
  const { locale, setLocale, t } = useLocale();
  return (
    <div className="space-y-4 rounded-2xl border border-[var(--brand-border)] bg-white p-6">
      <header>
        <h2 className="font-heading text-lg font-bold">{t("account.language.heading")}</h2>
        <p className="text-sm text-[var(--brand-muted)]">{t("account.language.subheading")}</p>
      </header>
      <LocaleSwitcher current={locale} onChange={setLocale} />
      <p className="text-xs text-[var(--brand-muted)]">{t("account.language.tip")}</p>
    </div>
  );
}

interface WalletState {
  wallet: {
    balanceThb: number;
    totalCreditedThb: number;
    totalSpentThb: number;
    updatedAt: string | null;
  };
  transactions: Array<{
    id: string;
    kind: "topup" | "promo" | "refund" | "unlock-episode" | "unlock-bundle" | "adjustment";
    amountThb: number;
    balanceAfterThb: number;
    refKind: string | null;
    refId: string | null;
    note: string | null;
    createdAt: string;
  }>;
}

const QUICK_AMOUNTS = [50, 100, 250, 500];

function WalletTab() {
  const { locale, t } = useLocale();
  const [state, setState] = useState<WalletState | null>(null);
  const [custom, setCustom] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    void api
      .get<WalletState>("/api/wallet")
      .then(setState)
      .catch(() =>
        setState({
          wallet: { balanceThb: 0, totalCreditedThb: 0, totalSpentThb: 0, updatedAt: null },
          transactions: [],
        }),
      );
  };
  useEffect(refresh, []);

  const topup = async (amountThb: number) => {
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.post<{ clientSecret: string; sessionId: string }>("/api/wallet/topup", {
        amountThb,
      });
      // Stripe Embedded Checkout — redirect to a dedicated /checkout page that
      // mounts the embedded form. For round-5 we route through the existing
      // /checkout/embedded route used by pack/bundle flows. Falls back to
      // alerting the clientSecret for the demo if the route doesn't exist.
      window.location.href = `/checkout/embedded?session=${res.sessionId}&kind=wallet-topup`;
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!state) {
    return (
      <div className="space-y-3">
        <div className="h-32 animate-pulse rounded-2xl bg-[var(--brand-pink-soft)]" />
        <div className="h-20 animate-pulse rounded-2xl bg-[var(--brand-pink-soft)]" />
      </div>
    );
  }

  const customAmount = Number(custom);
  const customValid = Number.isFinite(customAmount) && customAmount >= 10 && customAmount <= 5000;

  return (
    <div className="space-y-6">
      {/* Balance card */}
      <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-[var(--brand-rose)] to-[#f48ba6] p-6 text-white shadow-lg">
        <p className="text-xs font-semibold uppercase tracking-wider text-white/80">
          {t("account.wallet.balance_label")}
        </p>
        <p className="mt-2 font-mono text-4xl font-bold">
          ฿{state.wallet.balanceThb.toLocaleString()}
        </p>
        <p className="mt-1 text-xs text-white/80">
          {t("account.wallet.totals", {
            credited: state.wallet.totalCreditedThb,
            spent: state.wallet.totalSpentThb,
          })}
        </p>
      </div>

      {/* Top-up controls */}
      <div className="space-y-3 rounded-2xl border border-[var(--brand-border)] bg-white p-6">
        <h2 className="font-heading text-lg font-bold">{t("account.wallet.topup.heading")}</h2>
        <p className="text-sm text-[var(--brand-muted)]">{t("account.wallet.topup.subheading")}</p>
        <div className="grid grid-cols-4 gap-2">
          {QUICK_AMOUNTS.map((amt) => (
            <button
              key={amt}
              type="button"
              disabled={submitting}
              onClick={() => topup(amt)}
              className="rounded-full border border-[var(--brand-border)] bg-[var(--brand-pink-soft)] py-2 text-sm font-semibold text-[var(--brand-ink)] transition-colors hover:bg-[var(--brand-rose)] hover:text-white disabled:opacity-50"
            >
              ฿{amt}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[var(--brand-muted)]">฿</span>
          <input
            type="number"
            inputMode="numeric"
            min={10}
            max={5000}
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder={t("account.wallet.topup.custom_placeholder")}
            className="h-10 flex-1 rounded-xl border border-[var(--brand-border)] bg-white px-3 text-sm focus-visible:border-[var(--brand-rose)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-rose)]/20"
          />
          <Button
            disabled={!customValid || submitting}
            onClick={() => customValid && topup(customAmount)}
          >
            {t("account.wallet.topup.cta")}
          </Button>
        </div>
        {error ? (
          <p role="alert" className="text-sm text-[var(--brand-rose)]">
            {error}
          </p>
        ) : null}
      </div>

      {/* Ledger */}
      <div className="rounded-2xl border border-[var(--brand-border)] bg-white">
        <header className="border-b border-[var(--brand-border)] px-6 py-4">
          <h2 className="font-heading text-lg font-bold">{t("account.wallet.tx.heading")}</h2>
        </header>
        {state.transactions.length === 0 ? (
          <p className="px-6 py-8 text-center text-sm text-[var(--brand-muted)]">
            {t("account.wallet.tx.empty")}
          </p>
        ) : (
          <ul className="divide-y divide-[var(--brand-border)]">
            {state.transactions.map((tx) => {
              const positive = tx.amountThb > 0;
              const kindKey = {
                topup: "account.wallet.tx.kind.topup",
                promo: "account.wallet.tx.kind.promo",
                refund: "account.wallet.tx.kind.refund",
                "unlock-episode": "account.wallet.tx.kind.unlock_episode",
                "unlock-bundle": "account.wallet.tx.kind.unlock_bundle",
                adjustment: "account.wallet.tx.kind.adjustment",
              }[tx.kind];
              return (
                <li key={tx.id} className="flex items-center justify-between px-6 py-3">
                  <div>
                    <p className="text-sm font-semibold text-[var(--brand-ink)]">{t(kindKey)}</p>
                    <p className="text-xs text-[var(--brand-muted)]">
                      {tx.note ? `${tx.note} · ` : ""}
                      {new Date(tx.createdAt).toLocaleString(locale === "th" ? "th-TH" : "en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p
                      className={cn(
                        "font-mono text-sm font-semibold",
                        positive ? "text-emerald-700" : "text-[var(--brand-ink)]",
                      )}
                    >
                      {positive ? "+" : ""}฿{tx.amountThb}
                    </p>
                    <p className="text-xs text-[var(--brand-muted)]">
                      {t("account.wallet.tx.balance_label")} ฿{tx.balanceAfterThb}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// D2 — Watch history
// ===========================================================================

interface WatchHistoryItem {
  episodeId: string;
  seriesId: string;
  seriesSlug: string | null;
  seriesTitleEn: string | null;
  seriesTitleTh: string | null;
  seriesPosterUrl: string | null;
  seasonNumber: number | null;
  episodeNumber: number | null;
  episodeTitleEn: string | null;
  episodeTitleTh: string | null;
  episodeThumbnailUrl: string | null;
  positionSec: number;
  durationSec: number;
  completed: boolean;
  completedAt: string | null;
  updatedAt: string;
}

function WatchHistoryTab() {
  const { locale, t } = useLocale();
  const [items, setItems] = useState<WatchHistoryItem[] | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  const refresh = () => {
    void api
      .get<{ items: WatchHistoryItem[] }>("/api/me/watch-history?limit=200")
      .then((d) => setItems(d.items))
      .catch(() => setItems([]));
  };
  useEffect(refresh, []);

  const handleClearAll = async () => {
    setClearing(true);
    try {
      // Clearing every row — pass an empty body so the server treats it as
      // a "wipe-all" command (episodeId omitted).
      await api.post("/api/me/watch-history/clear", {});
      setItems([]);
      setConfirmOpen(false);
    } finally {
      setClearing(false);
    }
  };

  if (items === null) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-20 animate-pulse rounded-2xl bg-[var(--brand-pink-soft)]" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-[var(--brand-border)] p-12 text-center">
        <p className="font-heading text-lg font-bold">{t("account.history.empty.title")}</p>
        <p className="mt-1 text-sm text-[var(--brand-muted)]">
          {t("account.history.empty.description")}
        </p>
        <Link
          to="/series"
          className="mt-4 inline-flex h-10 items-center rounded-full bg-[var(--brand-rose)] px-5 text-sm font-semibold text-white"
        >
          {t("account.history.empty.cta")}
        </Link>
      </div>
    );
  }

  const continueItems = items.filter((it) => !it.completed).slice(0, 8);

  return (
    <div className="space-y-8">
      {/* Continue watching — 2-up on mobile, 4-up at sm+ */}
      {continueItems.length > 0 ? (
        <section>
          <h2 className="mb-3 font-heading text-lg font-bold">
            {t("account.history.continue_heading")}
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {continueItems.map((it) => (
              <ContinueCard key={it.episodeId} item={it} locale={locale} />
            ))}
          </div>
        </section>
      ) : null}

      {/* Full history */}
      <section>
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="font-heading text-lg font-bold">{t("account.history.all_heading")}</h2>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            className="text-xs font-semibold text-[var(--brand-rose)] hover:underline"
          >
            {t("account.history.clear_link")}
          </button>
        </div>
        <ul className="divide-y divide-[var(--brand-border)] overflow-hidden rounded-2xl border border-[var(--brand-border)] bg-white">
          {items.map((it) => (
            <HistoryRow key={it.episodeId} item={it} locale={locale} />
          ))}
        </ul>
      </section>

      {confirmOpen ? (
        <ConfirmModal
          title={t("account.history.confirm.title")}
          message={t("account.history.confirm.message")}
          confirmLabel={
            clearing
              ? t("account.history.confirm.confirm_busy")
              : t("account.history.confirm.confirm")
          }
          cancelLabel={t("account.history.confirm.cancel")}
          danger
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleClearAll}
          disabled={clearing}
        />
      ) : null}
    </div>
  );
}

function ContinueCard({ item, locale }: { item: WatchHistoryItem; locale: "th" | "en" }) {
  const title =
    (locale === "th" ? item.episodeTitleTh : item.episodeTitleEn) ?? item.episodeTitleEn ?? "—";
  const seriesTitle =
    (locale === "th" ? item.seriesTitleTh : item.seriesTitleEn) ?? item.seriesTitleEn ?? "";
  const pct =
    item.durationSec > 0
      ? Math.min(100, Math.round((item.positionSec / item.durationSec) * 100))
      : 0;
  const href =
    item.seriesSlug && item.seasonNumber != null && item.episodeNumber != null
      ? `/watch/${item.seriesSlug}/${item.seasonNumber}/${item.episodeNumber}`
      : "#";
  return (
    <Link
      to={href}
      className="group block overflow-hidden rounded-xl bg-[var(--brand-pink-soft)] text-left"
    >
      <div className="relative aspect-[9/16] overflow-hidden">
        {item.episodeThumbnailUrl ? (
          <img
            src={item.episodeThumbnailUrl}
            alt={title}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
            loading="lazy"
          />
        ) : item.seriesPosterUrl ? (
          <img
            src={item.seriesPosterUrl}
            alt={title}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full bg-[var(--brand-pink-soft)]" />
        )}
        {/* Progress bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/30">
          <div className="h-full bg-[var(--brand-rose)]" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="p-2">
        <p className="line-clamp-1 text-xs text-[var(--brand-muted)]">{seriesTitle}</p>
        <p className="line-clamp-1 text-sm font-semibold text-[var(--brand-ink)]">
          {item.seasonNumber != null && item.episodeNumber != null
            ? `S${item.seasonNumber}E${item.episodeNumber} · ${title}`
            : title}
        </p>
      </div>
    </Link>
  );
}

function HistoryRow({ item, locale }: { item: WatchHistoryItem; locale: "th" | "en" }) {
  const t = makeT(locale);
  const title =
    (locale === "th" ? item.episodeTitleTh : item.episodeTitleEn) ?? item.episodeTitleEn ?? "—";
  const seriesTitle =
    (locale === "th" ? item.seriesTitleTh : item.seriesTitleEn) ?? item.seriesTitleEn ?? "";
  const stamp = new Date(item.updatedAt).toLocaleString(locale === "th" ? "th-TH" : "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  const href =
    item.seriesSlug && item.seasonNumber != null && item.episodeNumber != null
      ? `/watch/${item.seriesSlug}/${item.seasonNumber}/${item.episodeNumber}`
      : "#";
  const pct =
    item.durationSec > 0
      ? Math.min(100, Math.round((item.positionSec / item.durationSec) * 100))
      : 0;
  return (
    <li className="flex items-center gap-3 p-3">
      <Link to={href} className="block shrink-0 overflow-hidden rounded-md">
        {item.episodeThumbnailUrl || item.seriesPosterUrl ? (
          <img
            src={item.episodeThumbnailUrl ?? item.seriesPosterUrl ?? ""}
            alt={title}
            className="h-14 w-24 object-cover"
            loading="lazy"
          />
        ) : (
          <div className="h-14 w-24 bg-[var(--brand-pink-soft)]" />
        )}
      </Link>
      <div className="min-w-0 flex-1">
        <p className="line-clamp-1 text-xs text-[var(--brand-muted)]">{seriesTitle}</p>
        <Link to={href} className="block text-sm font-semibold hover:underline">
          {item.seasonNumber != null && item.episodeNumber != null
            ? `S${item.seasonNumber}E${item.episodeNumber} · ${title}`
            : title}
        </Link>
        <p className="text-xs text-[var(--brand-muted)]">
          {stamp} · {item.completed ? t("account.history.status.completed") : `${pct}%`}
        </p>
      </div>
    </li>
  );
}

// ===========================================================================
// D3 — Devices
// ===========================================================================

interface SessionItem {
  sessionId: string;
  token: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  current: boolean;
  createdAt: string | null;
  expiresAt: string | null;
}

// Best-effort UA parser — every UA on earth is custom-shaped so we just
// grep for the obvious patterns and fall back to "Unknown device".
function parseUserAgent(ua: string | null): { label: string; os: string } {
  if (!ua) return { label: "Unknown device", os: "" };
  const os = /Windows/i.test(ua)
    ? "Windows"
    : /Mac OS X|Macintosh/i.test(ua)
      ? "macOS"
      : /iPhone|iPad|iOS/i.test(ua)
        ? "iOS"
        : /Android/i.test(ua)
          ? "Android"
          : /Linux/i.test(ua)
            ? "Linux"
            : "";
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /OPR\/|Opera/.test(ua)
      ? "Opera"
      : /Firefox\//.test(ua)
        ? "Firefox"
        : /Chrome\//.test(ua)
          ? "Chrome"
          : /Safari\//.test(ua)
            ? "Safari"
            : "Browser";
  return { label: `${browser}${os ? ` on ${os}` : ""}`, os };
}

function DevicesTab() {
  const { locale, t } = useLocale();
  const { logout } = useAuth();
  const [items, setItems] = useState<SessionItem[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = () => {
    void api
      .get<{ items: SessionItem[] }>("/api/me/sessions")
      .then((d) => setItems(d.items))
      .catch(() => setItems([]));
  };
  useEffect(refresh, []);

  const revoke = async (sessionId: string) => {
    setBusy(sessionId);
    try {
      await api.post(`/api/me/sessions/${encodeURIComponent(sessionId)}/revoke`);
      refresh();
    } finally {
      setBusy(null);
    }
  };

  const revokeAllOthers = async () => {
    setBusy("__others__");
    try {
      await api.post("/api/me/sessions/revoke-others");
      refresh();
    } finally {
      setBusy(null);
    }
  };

  if (items === null) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-20 animate-pulse rounded-2xl bg-[var(--brand-pink-soft)]" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="rounded-2xl border border-[var(--brand-border)] bg-white p-6">
        <h2 className="font-heading text-lg font-bold">{t("account.devices.heading")}</h2>
        <p className="mt-1 text-sm text-[var(--brand-muted)]">{t("account.devices.subheading")}</p>
        {items.length > 1 ? (
          <div className="mt-3">
            <Button variant="ghost" onClick={revokeAllOthers} disabled={busy === "__others__"}>
              {busy === "__others__"
                ? t("account.devices.signout_others_busy")
                : t("account.devices.signout_others")}
            </Button>
          </div>
        ) : null}
      </header>

      <ul className="overflow-hidden rounded-2xl border border-[var(--brand-border)] bg-white">
        {items.map((s) => {
          const parsed = parseUserAgent(s.userAgent);
          const last = s.createdAt
            ? new Date(s.createdAt).toLocaleString(locale === "th" ? "th-TH" : "en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "—";
          return (
            <li
              key={s.sessionId}
              className="flex items-center justify-between gap-3 border-b border-[var(--brand-border)] p-4 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <p className="font-semibold">
                  {parsed.label}
                  {s.current ? (
                    <span className="ml-2 inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                      {t("account.devices.this_device")}
                    </span>
                  ) : null}
                </p>
                <p className="font-mono text-xs text-[var(--brand-muted)]">
                  {s.ipAddress ?? "—"} · {last}
                </p>
              </div>
              <div className="shrink-0">
                {s.current ? (
                  <Button variant="ghost" onClick={logout}>
                    {t("account.devices.signout_current")}
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    disabled={busy === s.sessionId}
                    onClick={() => revoke(s.sessionId)}
                  >
                    {busy === s.sessionId
                      ? t("account.devices.signout_busy")
                      : t("account.devices.signout")}
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ===========================================================================
// D4 — Security: password + 2FA
// ===========================================================================

function SecurityTab() {
  const { t } = useLocale();
  const { user, refresh } = useAuth();

  // Password change state
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const onChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg(null);
    if (next.length < 8) {
      setPwMsg({ kind: "err", text: t("account.security.password.too_short") });
      return;
    }
    if (next !== confirm) {
      setPwMsg({ kind: "err", text: t("account.security.password.mismatch") });
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/api/me/password", {
        currentPassword: current,
        newPassword: next,
      });
      setPwMsg({ kind: "ok", text: t("account.security.password.updated") });
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? typeof err.body === "object" && err.body && "message" in err.body
            ? String((err.body as { message: unknown }).message)
            : err.message
          : (err as Error).message;
      setPwMsg({ kind: "err", text: msg });
    } finally {
      setPwBusy(false);
    }
  };

  // 2FA state
  const enabled = !!user?.twoFactorEnabled;
  const [tfBusy, setTfBusy] = useState(false);
  const [tfPassword, setTfPassword] = useState("");
  const [tfSetup, setTfSetup] = useState<{ totpURI: string; backupCodes: string[] } | null>(null);
  const [tfError, setTfError] = useState<string | null>(null);

  const enableTwoFactor = async () => {
    setTfError(null);
    setTfBusy(true);
    try {
      const res = await api.post<{ totpURI: string; backupCodes: string[] }>(
        "/api/me/two-factor/enable",
        { password: tfPassword },
      );
      setTfSetup(res);
      setTfPassword("");
      await refresh();
    } catch (err) {
      setTfError((err as Error).message);
    } finally {
      setTfBusy(false);
    }
  };

  const disableTwoFactor = async () => {
    setTfError(null);
    setTfBusy(true);
    try {
      await api.post("/api/me/two-factor/disable", { password: tfPassword });
      setTfPassword("");
      setTfSetup(null);
      await refresh();
    } catch (err) {
      setTfError((err as Error).message);
    } finally {
      setTfBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Password change */}
      <form
        onSubmit={onChangePassword}
        className="space-y-4 rounded-2xl border border-[var(--brand-border)] bg-white p-6"
      >
        <header>
          <h2 className="font-heading text-lg font-bold">
            {t("account.security.password.heading")}
          </h2>
          <p className="text-sm text-[var(--brand-muted)]">
            {t("account.security.password.subheading")}
          </p>
        </header>
        <PasswordField
          label={t("account.security.password.current")}
          value={current}
          onChange={setCurrent}
          autoComplete="current-password"
        />
        <PasswordField
          label={t("account.security.password.new")}
          value={next}
          onChange={setNext}
          autoComplete="new-password"
        />
        <PasswordField
          label={t("account.security.password.confirm")}
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
        />
        {pwMsg ? (
          <p
            role={pwMsg.kind === "err" ? "alert" : "status"}
            className={cn(
              "text-sm",
              pwMsg.kind === "err" ? "text-[var(--brand-rose)]" : "text-emerald-700",
            )}
          >
            {pwMsg.text}
          </p>
        ) : null}
        <Button type="submit" disabled={pwBusy || !current || !next}>
          {pwBusy ? t("account.security.password.saving") : t("account.security.password.save")}
        </Button>
      </form>

      {/* 2FA */}
      <div className="space-y-4 rounded-2xl border border-[var(--brand-border)] bg-white p-6">
        <header className="flex items-baseline justify-between gap-4">
          <div>
            <h2 className="font-heading text-lg font-bold">{t("account.security.2fa.heading")}</h2>
            <p className="text-sm text-[var(--brand-muted)]">
              {t("account.security.2fa.subheading")}
            </p>
          </div>
          <span
            className={cn(
              "shrink-0 rounded-full px-3 py-1 text-xs font-bold",
              enabled
                ? "bg-emerald-100 text-emerald-700"
                : "bg-[var(--brand-pink-soft)] text-[var(--brand-ink)]/70",
            )}
          >
            {enabled
              ? t("account.security.2fa.status.enabled")
              : t("account.security.2fa.status.off")}
          </span>
        </header>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[var(--brand-muted)]">
              {t("account.security.2fa.confirm_label")}
            </label>
            <input
              type="password"
              value={tfPassword}
              onChange={(e) => setTfPassword(e.target.value)}
              autoComplete="current-password"
              className="h-10 w-full rounded-xl border border-[var(--brand-border)] bg-white px-3 text-sm focus-visible:border-[var(--brand-rose)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-rose)]/20"
            />
          </div>
          {enabled ? (
            <Button onClick={disableTwoFactor} disabled={tfBusy || !tfPassword}>
              {tfBusy ? t("account.security.2fa.disabling") : t("account.security.2fa.disable")}
            </Button>
          ) : (
            <Button onClick={enableTwoFactor} disabled={tfBusy || !tfPassword}>
              {tfBusy ? t("account.security.2fa.enabling") : t("account.security.2fa.enable")}
            </Button>
          )}
        </div>

        {tfError ? (
          <p role="alert" className="text-sm text-[var(--brand-rose)]">
            {tfError}
          </p>
        ) : null}

        {tfSetup ? (
          <div className="space-y-3 rounded-xl bg-[var(--brand-pink-soft)] p-4 text-sm">
            <p className="font-semibold">{t("account.security.2fa.setup_heading")}</p>
            <p className="break-all font-mono text-xs text-[var(--brand-muted)]">
              {tfSetup.totpURI}
            </p>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-muted)]">
                {t("account.security.2fa.backup_codes")}
              </p>
              <ul className="mt-1 grid grid-cols-2 gap-1 font-mono text-xs sm:grid-cols-4">
                {tfSetup.backupCodes.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </div>
            <p className="text-xs text-[var(--brand-muted)]">
              {t("account.security.2fa.backup_codes_hint")}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function PasswordField({
  label,
  value,
  onChange,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[var(--brand-muted)]">
        {label}
      </span>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        className="h-10 w-full rounded-xl border border-[var(--brand-border)] bg-white px-3 text-sm focus-visible:border-[var(--brand-rose)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-rose)]/20"
      />
    </label>
  );
}

// ===========================================================================
// D5 — Notifications
// ===========================================================================

interface NotificationPrefs {
  marketing: boolean;
  newReleases: boolean;
  newsletter: boolean;
}

function NotificationsTab() {
  const { t } = useLocale();
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    void api
      .get<NotificationPrefs>("/api/me/notifications")
      .then(setPrefs)
      .catch(() => setPrefs({ marketing: true, newReleases: true, newsletter: false }));
  }, []);

  const persist = async (next: NotificationPrefs) => {
    setPrefs(next);
    setSaving(true);
    try {
      await api.post("/api/me/notifications", next);
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) {
    return <div className="h-40 animate-pulse rounded-2xl bg-[var(--brand-pink-soft)]" />;
  }

  const row = (field: keyof NotificationPrefs, titleKey: string, descKey: string) => (
    <li
      key={field}
      className="flex items-start justify-between gap-4 border-b border-[var(--brand-border)] p-5 last:border-b-0"
    >
      <div className="min-w-0">
        <p className="font-semibold">{t(titleKey)}</p>
        <p className="text-sm text-[var(--brand-muted)]">{t(descKey)}</p>
      </div>
      <Switch checked={prefs[field]} onCheckedChange={(v) => persist({ ...prefs, [field]: v })} />
    </li>
  );

  return (
    <div className="space-y-4">
      <ul className="overflow-hidden rounded-2xl border border-[var(--brand-border)] bg-white">
        {row(
          "marketing",
          "account.notifications.marketing.title",
          "account.notifications.marketing.desc",
        )}
        {row(
          "newReleases",
          "account.notifications.new_releases.title",
          "account.notifications.new_releases.desc",
        )}
        {row(
          "newsletter",
          "account.notifications.newsletter.title",
          "account.notifications.newsletter.desc",
        )}
      </ul>
      <p className="text-xs text-[var(--brand-muted)]">
        {saving
          ? t("account.notifications.saving")
          : savedAt
            ? t("account.notifications.saved")
            : t("account.notifications.auto_save_hint")}
      </p>
    </div>
  );
}

// ===========================================================================
// D6 — Data & privacy
// ===========================================================================

function DataPrivacyTab() {
  const { t } = useLocale();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [exportState, setExportState] = useState<{
    jobId: string;
    etaSec: number;
  } | null>(null);
  const [exporting, setExporting] = useState(false);

  const requestExport = async () => {
    setExporting(true);
    try {
      const res = await api.post<{ jobId: string; etaSec: number }>("/api/me/data-export");
      setExportState({ jobId: res.jobId, etaSec: res.etaSec });
    } finally {
      setExporting(false);
    }
  };

  // Delete-account modal state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [typedEmail, setTypedEmail] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);

  const canDelete = useMemo(
    () => typedEmail.trim().toLowerCase() === (user?.email ?? "").toLowerCase(),
    [typedEmail, user?.email],
  );

  const runDelete = async () => {
    if (!canDelete) return;
    setDeleting(true);
    setDeleteErr(null);
    try {
      // DELETE with body — our api.delete helper doesn't send one, so call
      // fetch directly here. The server requires { confirmEmail } in body.
      const res = await fetch("/api/me", {
        method: "DELETE",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ confirmEmail: typedEmail.trim() }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
        throw new Error(body.message ?? body.error ?? "delete_failed");
      }
      await logout();
      navigate("/", { replace: true });
    } catch (err) {
      setDeleteErr((err as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Export */}
      <div className="rounded-2xl border border-[var(--brand-border)] bg-white p-6">
        <h2 className="font-heading text-lg font-bold">{t("account.privacy.export.heading")}</h2>
        <p className="mt-1 text-sm text-[var(--brand-muted)]">
          {t("account.privacy.export.subheading")}
        </p>
        {exportState ? (
          <div className="mt-3 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-800">
            <p className="font-semibold">{t("account.privacy.export.queued")}</p>
            <p className="font-mono text-xs">job {exportState.jobId}</p>
            <p className="mt-1">
              {t("account.privacy.export.eta", { hours: Math.round(exportState.etaSec / 3600) })}
            </p>
          </div>
        ) : (
          <Button className="mt-3" onClick={requestExport} disabled={exporting}>
            {exporting ? t("account.privacy.export.busy") : t("account.privacy.export.cta")}
          </Button>
        )}
      </div>

      {/* Delete */}
      <div className="rounded-2xl border border-[var(--brand-border)] bg-white p-6">
        <h2 className="font-heading text-lg font-bold text-[var(--brand-rose)]">
          {t("account.privacy.delete.heading")}
        </h2>
        <p className="mt-1 text-sm text-[var(--brand-muted)]">
          {t("account.privacy.delete.subheading")}
        </p>
        <Button className="mt-3" variant="ghost" onClick={() => setDeleteOpen(true)}>
          {t("account.privacy.delete.cta")}
        </Button>
      </div>

      {deleteOpen ? (
        <ConfirmModal
          title={t("account.privacy.delete.confirm_title")}
          message={t("account.privacy.delete.confirm_message", { email: user?.email ?? "" })}
          confirmLabel={
            deleting ? t("account.privacy.delete.deleting") : t("account.privacy.delete.confirm")
          }
          cancelLabel={t("account.privacy.delete.cancel")}
          danger
          onCancel={() => {
            setDeleteOpen(false);
            setTypedEmail("");
            setDeleteErr(null);
          }}
          onConfirm={runDelete}
          disabled={deleting || !canDelete}
        >
          <input
            type="email"
            value={typedEmail}
            onChange={(e) => setTypedEmail(e.target.value)}
            placeholder={user?.email ?? ""}
            className="mt-3 h-10 w-full rounded-xl border border-[var(--brand-border)] bg-white px-3 text-sm focus-visible:border-[var(--brand-rose)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-rose)]/20"
            autoFocus
          />
          {deleteErr ? (
            <p role="alert" className="mt-2 text-sm text-[var(--brand-rose)]">
              {deleteErr}
            </p>
          ) : null}
        </ConfirmModal>
      ) : null}
    </div>
  );
}

// ===========================================================================
// Shared confirm modal — lightweight, no Dialog primitive to avoid the
// react-router-dialog focus-trap dance for one-shot confirmations.
// ===========================================================================

function ConfirmModal({
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  danger,
  disabled,
  children,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  danger?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <h3 className="font-heading text-lg font-bold">{title}</h3>
        <p className="mt-2 text-sm text-[var(--brand-muted)]">{message}</p>
        {children}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            onClick={() => void onConfirm()}
            disabled={disabled}
            className={cn(danger && "bg-[var(--brand-rose)] hover:bg-[var(--brand-rose)]/90")}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
